import { ExpressionSpecification, LayerSpecification } from "mapbox-gl";

import GaiaMapStyle from "~/modules/map/components/GaiaMapStyle";
import { MAP_LEGEND_ICON_SIZE } from "~/modules/project/screens/ProjectDeliverablesScreen/components/ProjectDeliverablesMapLegend";

import { LAYER_DEFAULTS } from "./layerDefaults";

interface GaiaMapStyleLineProps {
    // Required
    id: string;
    source: string;

    // Line styling
    width?: number;
    color?: ExpressionSpecification | string;
    colorProperty?: string;

    // Border (optional)
    border?: {
        width?: number;
        color?: ExpressionSpecification | string;
    };

    // Dash pattern (optional)
    dashArray?: ExpressionSpecification | number[];

    // Icons repeated along the line (optional)
    icons?: {
        imageProperty: string;
        labelProperty?: string;
        size?: number;
        spacing?: number;
        offset?: [number, number];
    };

    // Common
    filter?: ExpressionSpecification;
    paint?: Record<string, any>;
    layout?: Record<string, any>;
}

export function GaiaMapStyleLine({
    id,
    source,
    width = LAYER_DEFAULTS.line.width,
    color,
    colorProperty,
    border,
    dashArray,
    icons,
    filter,
    paint,
    layout,
}: GaiaMapStyleLineProps) {
    const lineColor: ExpressionSpecification | string | undefined = colorProperty ? ["get", colorProperty] : color;

    const layers: LayerSpecification[] = [
        {
            id,
            source,
            type: "line",
            slot: LAYER_DEFAULTS.slot,
            ...(filter && { filter }),
            ...(layout && { layout }),
            paint: {
                "line-width": width,
                ...(lineColor && { "line-color": lineColor }),
                ...(border?.width && { "line-border-width": border.width }),
                ...(border?.color && { "line-border-color": border.color }),
                ...(dashArray && { "line-dasharray": dashArray }),
                ...paint,
            },
        },
        ...(icons ? [createIconLayer(id, source, icons, lineColor, filter)] : []),
    ];

    return <GaiaMapStyle layers={layers} />;
}

function createIconLayer(
    id: string,
    source: string,
    icons: NonNullable<GaiaMapStyleLineProps["icons"]>,
    color: ExpressionSpecification | string | undefined,
    filter: ExpressionSpecification | undefined,
): LayerSpecification {
    return {
        id: `${id}_icon`,
        source,
        type: "symbol",
        slot: LAYER_DEFAULTS.slot,
        ...(filter && { filter }),
        layout: {
            "symbol-placement": "line",
            "symbol-spacing": icons.spacing ?? LAYER_DEFAULTS.lineIcons.spacing,
            "icon-image": ["get", icons.imageProperty],
            "icon-size": icons.size ?? LAYER_DEFAULTS.lineIcons.size,
            "icon-offset": icons.offset ?? LAYER_DEFAULTS.lineIcons.offset,
            "icon-rotation-alignment": "map",
            "icon-keep-upright": true,
            "icon-allow-overlap": false,
            ...(icons.labelProperty && {
                "text-field": ["get", icons.labelProperty],
                "text-font": LAYER_DEFAULTS.textLayout["text-font"],
                "text-size": LAYER_DEFAULTS.lineIcons.textSize,
                "text-anchor": "left",
                "text-offset": LAYER_DEFAULTS.lineIcons.textOffset,
                "text-optional": true,
            }),
        },
        paint: {
            ...(color && { "icon-color": color, "text-color": color }),
        },
    };
}

export function GaiaMapStyleLineLegendIcon() {
    return (
        <svg width={MAP_LEGEND_ICON_SIZE} height={MAP_LEGEND_ICON_SIZE} viewBox="0 0 16 16" aria-hidden="true">
            <line x1="1" y1="8" x2="15" y2="8" stroke="#000000" strokeWidth={3} strokeDasharray="1 5" strokeLinecap="round" />
        </svg>
    );
}
